import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useUnsavedChanges } from '@/context/UnsavedChangesContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Layers, Plus, Trash2, Save as SaveIcon, FileDown, CheckCircle2, AlertCircle, SlidersHorizontal, Ruler } from 'lucide-react';
import { toast } from 'sonner';

const emptyRow = (from = '') => ({ from, to: '', color: '', grain_size: '', material: '' });

/**
 * EditableCombobox — Alian: "no me está dejando poner letra, tengo que hacerle
 * click", "no me está dejando irme con el tab". Replaces the old <Select>
 * (Color/Grain) and Popover+Checkbox (Material) with a real <input> so the
 * user can TYPE directly, tab through the whole row without extra clicks,
 * and still get a filtered suggestion list from /cutting-log/options — while
 * accepting any free-text value that isn't in the list.
 * `multi` (Material column) keeps the existing "Sand/Clay/Shell" behavior:
 * picking a suggestion appends it (joined by "/") instead of replacing.
 */
function EditableCombobox({ value, onChange, options = [], placeholder = '', testId, multi = false, className = '', disabled = false, size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const blurTimer = useRef(null);
  // `size="lg"` — mobile card rows (Alian: filas usables en el celular sin
  // inputs diminutos): input de 44px+ con texto 16px (evita el zoom
  // automático de iOS al enfocar) y opciones más altas para el dedo.
  const inputSizeClass = size === 'lg' ? 'h-11 text-base' : 'h-9 text-xs';
  const optionSizeClass = size === 'lg' ? 'px-3 py-2.5 text-sm' : 'px-2.5 py-1.5 text-xs';
  const text = value || '';
  const lastToken = multi ? (text.split('/').pop() || '').trim() : text.trim();
  const completedTokens = multi ? text.split('/').map(s => s.trim()).filter(Boolean) : [];
  const filtered = (options || []).filter(o => {
    if (multi && completedTokens.includes(o) && o.toLowerCase() !== lastToken.toLowerCase()) return false;
    if (!lastToken) return true;
    return o.toLowerCase().includes(lastToken.toLowerCase()) && o.toLowerCase() !== lastToken.toLowerCase();
  }).slice(0, 8);
  // Active option: clamp to a valid index so the top match is highlighted by
  // default (Alian: escribe "bl" -> "Black" ya sale resaltado, listo para
  // Enter) and arrow keys move within bounds.
  const activeIndex = filtered.length ? Math.min(Math.max(highlighted, 0), filtered.length - 1) : -1;

  const handlePick = (opt) => {
    if (multi) {
      const endsWithSep = text === '' || /\/\s*$/.test(text);
      const tokens = text.split('/').map(s => s.trim()).filter(Boolean);
      if (!endsWithSep && tokens.length) tokens.pop();
      tokens.push(opt);
      onChange(`${tokens.join('/')}/`);
    } else {
      onChange(opt);
      setOpen(false);
    }
    setHighlighted(-1);
  };

  const handleBlur = () => {
    blurTimer.current = setTimeout(() => {
      setOpen(false);
      if (multi) {
        const clean = text.split('/').map(s => s.trim()).filter(Boolean).join('/');
        if (clean !== text) onChange(clean);
      }
    }, 150);
  };

  const handleFocus = () => {
    if (disabled) return;
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setOpen(true);
  };

  // Alian: "me hace falta poderlo hacer con la flechita del tablero... para
  // no tener que levantar la mano del teclado." Arrow Down/Up move through
  // the suggestion list, Enter picks the highlighted one — Tab keeps its
  // native browser behavior to jump to the next field in the row.
  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (filtered.length) setHighlighted(prev => Math.min((prev < 0 ? 0 : prev + 1), filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length) setHighlighted(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && filtered[activeIndex]) {
        e.preventDefault();
        handlePick(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        className={`${inputSizeClass} w-full`}
        value={text}
        onChange={e => { onChange(e.target.value); setHighlighted(-1); }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        data-testid={testId}
      />
      {open && !disabled && filtered.length > 0 && (
        <div className="absolute z-20 top-full left-0 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg" data-testid={`${testId}-suggestions`}>
          {filtered.map((opt, i) => (
            <button
              key={opt}
              type="button"
              onMouseDown={e => { e.preventDefault(); handlePick(opt); }}
              onMouseEnter={() => setHighlighted(i)}
              className={`w-full text-left text-gray-700 ${optionSizeClass} ${i === activeIndex ? 'bg-blue-50 text-[#003366] font-medium' : 'hover:bg-gray-50'}`}
              data-testid={`${testId}-option-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * CuttingLogCard — Drill Cuttings Log (well log) for Well Construction jobs.
 * Repeatable rows: From / To / Color / Grain Size / Material — with configurable
 * dropdowns (Color & Material lists come from /cutting-log/options).
 *
 * The job CANNOT be marked Complete until at least one row has From, To & Material.
 */
export default forwardRef(function CuttingLogCard({ job, onSaved }, ref) {
  const { user } = useAuth();
  const { setDirty } = useUnsavedChanges();
  const isAdmin = ['admin', 'superadmin'].includes(user?.role);
  // Alian: la Oficina puede VER el Cutting Log pero no editarlo — solo el
  // Driller carga esos datos, Admin/Superadmin pueden corregir si hace falta.
  const readOnly = user?.role === 'office';
  const [options, setOptions] = useState({ colors: [], grain_sizes: [], materials: [] });
  const [rows, setRows] = useState(job.cutting_log?.length ? job.cutting_log : [emptyRow('0')]);
  // Well Casing (Alian: "las 3 fichas más importantes", after the Cutting Log).
  // `well_depth`/`casing_depth` are the same stored fields as before (relabeled
  // Total Depth / Total Casing Depth) so no historical data is lost.
  // `casing_diameter` is new. `drilled_depth` stays in the payload untouched —
  // it is NOT edited here anymore (it feeds the official Completion Report
  // form separately, and Total Depth != Drilled Depth: a well can be drilled
  // deeper than it ends up cased/finished).
  const [summary, setSummary] = useState({
    well_depth: job.cutting_log_summary?.well_depth || '',
    drilled_depth: job.cutting_log_summary?.drilled_depth || '',
    casing_depth: job.cutting_log_summary?.casing_depth || '',
    casing_diameter: job.cutting_log_summary?.casing_diameter || '',
    // Prompt AD (2026-09-21): "ACTUAL WELL CONSTRUCTION (Field Entry)" —
    // se acomoda el mockup sumando estos 3 campos al mismo bloque existente.
    screen_interval: job.cutting_log_summary?.screen_interval || '',
    gravel_pack: job.cutting_log_summary?.gravel_pack || '',
    cement_seal: job.cutting_log_summary?.cement_seal || '',
    notes: job.cutting_log_summary?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [optDraft, setOptDraft] = useState({ colors: '', grain_sizes: '', materials: '' });
  const [optSaving, setOptSaving] = useState(false);

  // TEMA C2/C3: el driller nunca debe perder filas cargadas — mantenemos una
  // foto de lo último guardado (manual, automático, o desde el botón "Save"
  // del encabezado del trabajo) para saber si hay cambios sin guardar, avisar
  // antes de salir de la pantalla, y disparar el autoguardado al salir de
  // una fila.
  const cleanRows = (source = rows) => {
    const clean = source.filter(r => String(r.from).trim() || String(r.to).trim() || r.material || r.color);
    return clean.length ? clean : [];
  };
  const lastSavedRef = useRef({ rows: cleanRows(rows), summary });
  const autosaveTimer = useRef(null);
  const justSavedTimer = useRef(null);

  const isDirty = () => (
    JSON.stringify(cleanRows()) !== JSON.stringify(lastSavedRef.current.rows) ||
    JSON.stringify(summary) !== JSON.stringify(lastSavedRef.current.summary)
  );

  useEffect(() => {
    if (readOnly) return;
    setDirty(isDirty(), 'Hay filas del Cutting Log sin guardar. ¿Salir sin guardar?');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, summary]);

  // Clear the dirty flag if the user leaves this job's page entirely.
  useEffect(() => () => setDirty(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const markSaved = (updatedJob, { silent = false } = {}) => {
    lastSavedRef.current = { rows: cleanRows(), summary };
    setDirty(false);
    setJustSaved(true);
    if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
    justSavedTimer.current = setTimeout(() => setJustSaved(false), 4000);
    if (onSaved && updatedJob) onSaved(updatedJob);
    if (!silent) toast.success('Core Material guardado');
  };

  const doSave = async ({ silent = false } = {}) => {
    if (readOnly) return null;
    setSaving(true);
    try {
      const clean = cleanRows();
      const res = await api.put(`/jobs/${job.id}`, { cutting_log: clean, cutting_log_summary: summary });
      markSaved(res.data, { silent });
      return res.data;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Error al guardar');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => doSave({ silent: false });

  // TEMA C3 — autoguardado al salir de una fila (blur), sin que el driller
  // tenga que acordarse de tocar un botón. Debounced para no disparar un
  // PUT por cada Tab entre columnas de la misma fila; el timer NO se cancela
  // al desmontar (si el usuario ya navegó a otra pantalla, el guardado en
  // curso sigue completándose en el servidor).
  const scheduleAutosave = () => {
    if (readOnly) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (isDirty()) doSave({ silent: true }).catch(() => {});
    }, 700);
  };

  // Exposed to JobDetailPage: the header "Save" button (next to Permit #)
  // now saves the job AND the Cutting Log/Well Casing in one action (TEMA
  // C1), instead of leaving the Cutting Log's local draft unsaved.
  useImperativeHandle(ref, () => ({
    getDraft: () => ({ cutting_log: cleanRows(), cutting_log_summary: summary }),
    markSaved,
    isDirty,
  }));

  const loadOptions = () => api.get('/cutting-log/options')
    .then(res => setOptions(res.data))
    .catch(() => setOptions({ colors: [], grain_sizes: ['Fine', 'Medium', 'Coarse'], materials: [] }));

  useEffect(() => { loadOptions(); }, []);

  const openOptionsEditor = () => {
    setOptDraft({
      colors: (options.colors || []).join('\n'),
      grain_sizes: (options.grain_sizes || []).join('\n'),
      materials: (options.materials || []).join('\n'),
    });
    setOptionsOpen(true);
  };

  const saveOptions = async () => {
    setOptSaving(true);
    try {
      const toList = (s) => s.split('\n').map(x => x.trim()).filter(Boolean);
      const res = await api.put('/cutting-log/options', {
        colors: toList(optDraft.colors),
        grain_sizes: toList(optDraft.grain_sizes),
        materials: toList(optDraft.materials),
      });
      setOptions(res.data);
      toast.success('Listas actualizadas');
      setOptionsOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar listas');
    } finally {
      setOptSaving(false);
    }
  };

  const updateRow = (idx, field, val) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };
  const addRow = () => {
    // Functional update — always reads the freshest row state (not a
    // possibly-stale closure) so the new row's "From" reliably picks up the
    // previous row's "To" (Alian: "agregué un row y no me da el 20").
    setRows(prev => {
      const last = prev[prev.length - 1];
      return [...prev, emptyRow(last?.to || '')];
    });
  };
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  const completeRows = rows.filter(r => String(r.from).trim() && String(r.to).trim() && String(r.material).trim());
  const isComplete = completeRows.length > 0;

  const downloadPdf = async () => {
    const tId = toast.loading('Generando Completion Report PDF…');
    try {
      const res = await api.get(`/jobs/${job.id}/completion-report-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CompletionReport_${job.job_number || job.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF generado', { id: tId });
    } catch {
      toast.error('Error generando el PDF', { id: tId });
    }
  };

  return (
    <Card className="bg-white border-gray-100 shadow-sm" data-testid="cutting-log-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#3399FF]" /> Cutting Log
          </CardTitle>
          <div className="flex items-center gap-2">
            <span
              data-testid="cutting-log-status"
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${isComplete ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}
            >
              {isComplete ? <><CheckCircle2 className="w-3 h-3" /> Complete</> : <><AlertCircle className="w-3 h-3" /> Pending</>}
            </span>
            {isAdmin && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500" onClick={openOptionsEditor} data-testid="cutting-log-options-btn" title="Editar listas">
                <SlidersHorizontal className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={downloadPdf} data-testid="download-completion-report-btn">
              <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {readOnly ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 mb-1" data-testid="cutting-log-readonly-banner">
            <p className="text-[11px] text-gray-500">
              Solo lectura — el Cutting Log lo edita el Driller (o Admin) desde Well Construction.
            </p>
          </div>
        ) : (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 mb-1">
          <p className="text-[11px] text-blue-700">
            Registra las formaciones cada 20 ft o en cada cambio. El trabajo no se puede cerrar hasta completar el Cutting Log.
          </p>
        </div>
        )}

        {/* Desktop header — 13 columns: last 2 reserved for the row actions
            (delete + the inline "add row" button that lives on the last row). */}
        <div className="hidden sm:grid grid-cols-[repeat(13,minmax(0,1fr))] gap-1.5 text-[10px] font-semibold text-gray-500 uppercase px-1">
          <span className="col-span-2">From (ft)</span>
          <span className="col-span-2">To (ft)</span>
          <span className="col-span-3">Color</span>
          <span className="col-span-2">Grain</span>
          <span className="col-span-2">Material</span>
          <span className="col-span-2"></span>
        </div>

        <div className="space-y-2" data-testid="cutting-log-rows">
          {rows.map((row, idx) => (
            <div key={idx} data-testid={`cutting-log-row-${idx}`}>
              {/* Desktop */}
              <div className="hidden sm:grid grid-cols-[repeat(13,minmax(0,1fr))] gap-1.5 items-center" onBlur={scheduleAutosave}>
                <Input className="col-span-2 h-9 text-xs text-center" type="number" value={row.from} onChange={e => updateRow(idx, 'from', e.target.value)} placeholder="0" data-testid={`cutting-from-${idx}`} disabled={readOnly} />
                <Input className="col-span-2 h-9 text-xs text-center" type="number" value={row.to} onChange={e => updateRow(idx, 'to', e.target.value)} placeholder="20" data-testid={`cutting-to-${idx}`} disabled={readOnly} />
                <EditableCombobox
                  value={row.color}
                  options={options.colors}
                  onChange={v => updateRow(idx, 'color', v)}
                  placeholder="Color"
                  testId={`cutting-color-${idx}`}
                  className="col-span-3"
                  disabled={readOnly}
                />
                <EditableCombobox
                  value={row.grain_size}
                  options={options.grain_sizes}
                  onChange={v => updateRow(idx, 'grain_size', v)}
                  placeholder="F/M/C"
                  testId={`cutting-grain-${idx}`}
                  className="col-span-2"
                  disabled={readOnly}
                />
                <EditableCombobox
                  value={row.material}
                  options={options.materials}
                  onChange={v => updateRow(idx, 'material', v)}
                  placeholder="Material"
                  testId={`cutting-material-${idx}`}
                  multi
                  className="col-span-2"
                  disabled={readOnly}
                />
                <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9 w-9 text-red-400" onClick={() => removeRow(idx)} disabled={readOnly || rows.length <= 1}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
                {!readOnly && idx === rows.length - 1 && (
                  <Button
                    type="button"
                    size="icon"
                    className="col-span-1 h-9 w-9 bg-[#003366] hover:bg-[#004080] text-white"
                    onClick={addRow}
                    title="Agregar fila"
                    data-testid="cutting-add-row-inline"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              {/* Mobile — card por capa: inputs de 44px+ y teclado numérico
                  para From/To (Alian: usarlo con el pulgar en el celular). */}
              <div className="sm:hidden border border-gray-200 rounded-lg p-3 space-y-2.5" onBlur={scheduleAutosave}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Layer {idx + 1}</span>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-red-400" onClick={() => removeRow(idx)} disabled={readOnly || rows.length <= 1} data-testid={`cutting-remove-row-mobile-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {!readOnly && idx === rows.length - 1 && (
                      <Button type="button" size="icon" className="h-11 w-11 bg-[#003366] hover:bg-[#004080] text-white" onClick={addRow} data-testid="cutting-add-row-inline-mobile">
                        <Plus className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="space-y-1"><Label className="text-[10px] text-gray-500 uppercase">From (ft)</Label><Input className="h-11 text-base" type="number" inputMode="decimal" value={row.from} onChange={e => updateRow(idx, 'from', e.target.value)} disabled={readOnly} data-testid={`cutting-from-mobile-${idx}`} /></div>
                  <div className="space-y-1"><Label className="text-[10px] text-gray-500 uppercase">To (ft)</Label><Input className="h-11 text-base" type="number" inputMode="decimal" value={row.to} onChange={e => updateRow(idx, 'to', e.target.value)} disabled={readOnly} data-testid={`cutting-to-mobile-${idx}`} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="space-y-1"><Label className="text-[10px] text-gray-500 uppercase">Color</Label>
                    <EditableCombobox value={row.color} options={options.colors} onChange={v => updateRow(idx, 'color', v)} placeholder="-" testId={`cutting-color-mobile-${idx}`} className="w-full" disabled={readOnly} size="lg" />
                  </div>
                  <div className="space-y-1"><Label className="text-[10px] text-gray-500 uppercase">Grain</Label>
                    <EditableCombobox value={row.grain_size} options={options.grain_sizes} onChange={v => updateRow(idx, 'grain_size', v)} placeholder="-" testId={`cutting-grain-mobile-${idx}`} className="w-full" disabled={readOnly} size="lg" />
                  </div>
                </div>
                <div className="space-y-1"><Label className="text-[10px] text-gray-500 uppercase">Material</Label>
                  <EditableCombobox
                    value={row.material}
                    options={options.materials}
                    onChange={v => updateRow(idx, 'material', v)}
                    placeholder="Material"
                    testId={`cutting-material-mobile-${idx}`}
                    multi
                    className="w-full"
                    disabled={readOnly}
                    size="lg"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Well Casing — Alian: "las 3 fichas más importantes", right after the
            Cutting Log rows. Casing Diameter is new; Total Depth / Total Casing
            Depth reuse the well_depth/casing_depth fields already stored (just
            relabeled) so no historical data is lost. */}
        <div className="pt-2 border-t border-gray-100" onBlur={scheduleAutosave}>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Ruler className="w-3.5 h-3.5 text-[#3399FF]" /> Actual Well Construction (Field Entry)
          </p>
          <div className="grid grid-cols-3 gap-2 sm:gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">Actual Casing Size (in)</Label>
              <Input className="h-11 sm:h-9 text-base sm:text-xs" type="number" inputMode="decimal" value={summary.casing_diameter} onChange={e => setSummary({ ...summary, casing_diameter: e.target.value })} placeholder="4" data-testid="cutting-summary-casing-diameter" disabled={readOnly} />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">Actual Total Depth (ft)</Label>
              <Input className="h-11 sm:h-9 text-base sm:text-xs" type="number" inputMode="decimal" value={summary.well_depth} onChange={e => setSummary({ ...summary, well_depth: e.target.value })} placeholder="240" data-testid="cutting-summary-well-depth" disabled={readOnly} />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">Actual Casing Depth (ft)</Label>
              <Input className="h-11 sm:h-9 text-base sm:text-xs" type="number" inputMode="decimal" value={summary.casing_depth} onChange={e => setSummary({ ...summary, casing_depth: e.target.value })} placeholder="185" data-testid="cutting-summary-casing-depth" disabled={readOnly} />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">Screen Interval</Label>
              <Input className="h-11 sm:h-9 text-base sm:text-xs" value={summary.screen_interval} onChange={e => setSummary({ ...summary, screen_interval: e.target.value })} placeholder="e.g. 180-200 ft" data-testid="cutting-summary-screen-interval" disabled={readOnly} />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">Gravel Pack</Label>
              <Input className="h-11 sm:h-9 text-base sm:text-xs" value={summary.gravel_pack} onChange={e => setSummary({ ...summary, gravel_pack: e.target.value })} data-testid="cutting-summary-gravel-pack" disabled={readOnly} />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">Cement/Seal</Label>
              <Input className="h-11 sm:h-9 text-base sm:text-xs" value={summary.cement_seal} onChange={e => setSummary({ ...summary, cement_seal: e.target.value })} data-testid="cutting-summary-cement-seal" disabled={readOnly} />
            </div>
          </div>
          <div className="space-y-1 mt-2">
            <Label className="text-[10px] text-gray-500 uppercase">Notes</Label>
            <Textarea rows={2} className="text-xs" value={summary.notes} onChange={e => setSummary({ ...summary, notes: e.target.value })} disabled={readOnly} data-testid="cutting-summary-notes" />
          </div>
        </div>

        {!readOnly && (
        <div className="pt-1 flex items-center gap-3 flex-wrap">
          <Button type="button" size="sm" className="flex-1 h-11 sm:h-9 text-sm sm:text-xs bg-[#003366] hover:bg-[#004080] text-white" onClick={handleSave} disabled={saving} data-testid="cutting-save-btn">
            <SaveIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar Core Material'}
          </Button>
          {/* TEMA C3 — aviso de guardado visible ABAJO, donde el driller está
              trabajando, no solo arriba a la derecha (toast). */}
          {justSaved && (
            <span className="text-xs font-medium text-emerald-600 flex items-center gap-1" data-testid="cutting-log-saved-indicator">
              <CheckCircle2 className="w-3.5 h-3.5" /> Guardado
            </span>
          )}
        </div>
        )}
      </CardContent>

      <Dialog open={optionsOpen} onOpenChange={setOptionsOpen}>
        <DialogContent className="max-w-lg" data-testid="cutting-log-options-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-[#003366]" /> Editar listas del Cutting Log
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500">Un valor por línea. Estas listas alimentan los dropdowns de Color, Grano y Material.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Colores</Label>
              <Textarea rows={8} value={optDraft.colors} onChange={e => setOptDraft({ ...optDraft, colors: e.target.value })} className="text-xs font-mono" data-testid="opt-colors" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Grano</Label>
              <Textarea rows={8} value={optDraft.grain_sizes} onChange={e => setOptDraft({ ...optDraft, grain_sizes: e.target.value })} className="text-xs font-mono" data-testid="opt-grain" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Materiales</Label>
              <Textarea rows={8} value={optDraft.materials} onChange={e => setOptDraft({ ...optDraft, materials: e.target.value })} className="text-xs font-mono" data-testid="opt-materials" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOptionsOpen(false)}>Cancelar</Button>
            <Button className="bg-[#003366] hover:bg-[#004080] text-white" onClick={saveOptions} disabled={optSaving} data-testid="opt-save">
              <SaveIcon className="w-4 h-4 mr-1.5" /> {optSaving ? 'Guardando…' : 'Guardar listas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
});
